// Procedural, seeded silhouettes for the parallax layers. Every path is drawn
// in a viewBox that is `width` px wide and 100 units tall; the SVG stretches
// vertically to whatever height the layer is given.

function rng(seed) {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Smooth 1D value noise from a list of random control points.
function noise1d(rand, count) {
  const pts = Array.from({ length: count + 2 }, rand)
  return (u) => {
    const f = u * count
    const i = Math.floor(f)
    const t = f - i
    const k = t * t * (3 - 2 * t)
    return pts[i] * (1 - k) + pts[i + 1] * k
  }
}

// Mountain ridge: `base` is the average ridge height (0–100 from the bottom),
// `amp` the vertical range, `roughness` how many peaks per 1000px.
export function ridgePath(width, { seed, base, amp, roughness, jagged = 0 }) {
  const rand = rng(seed)
  const peaks = Math.max(2, Math.round((width / 1000) * roughness))
  const big = noise1d(rand, peaks)
  const small = noise1d(rand, peaks * 4)
  const step = 8
  let d = `M0 100`
  for (let x = 0; x <= width; x += step) {
    const u = x / width
    const h = base + amp * (big(u) - 0.5) * 2 + amp * jagged * (small(u) - 0.5)
    d += ` L${x} ${(100 - h).toFixed(2)}`
  }
  return d + ` L${width} 100 Z`
}

// A band of pine trees standing on a gently rolling line.
export function pinesPath(width, { seed, base, minH, maxH }) {
  const rand = rng(seed)
  const roll = noise1d(rand, Math.max(2, Math.round(width / 600)))
  let d = `M0 100 L0 ${100 - base}`
  let x = 0
  while (x < width) {
    const w = 22 + rand() * 20
    const h = minH + rand() * (maxH - minH)
    const ground = 100 - base - roll(x / width) * 6
    // Two tiers read as a conifer at silhouette scale.
    const pt = (fx, fy) => ` L${(x + w * fx).toFixed(1)} ${(ground - h * fy).toFixed(1)}`
    d += pt(0, 0) + pt(0.18, 0.42) + pt(0.32, 0.46) + pt(0.5, 1) + pt(0.68, 0.46) + pt(0.82, 0.42) + pt(1, 0)
    x += w * (0.45 + rand() * 0.45)
  }
  return d + ` L${width} ${100 - base} L${width} 100 Z`
}

// Soft rounded blobs (bushes, grass tufts) for the blurred foreground.
export function blobsPath(width, { seed, base, minR, maxR, gap, squash = 0.3 }) {
  const rand = rng(seed)
  let d = ''
  let x = rand() * gap
  while (x < width) {
    const r = minR + rand() * (maxR - minR)
    const cy = 100 - base
    d += `M${x - r} ${cy} a${r} ${r * squash} 0 0 1 ${r * 2} 0 Z `
    x += gap * (0.5 + rand())
  }
  return d + `M0 ${100 - base} H${width} V100 H0 Z`
}
